import React from 'react';
import classNames from 'classnames';

import {
  createSlug,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
} from '../../util/urlHelpers';
import { LISTING_STATE_DRAFT } from '../../util/types';
import { PROVINCIA_LABELS } from '../../util/listingFieldLabels';
import ListingFieldsGrid from '../ListingFieldsGrid';
import NamedLink from '../NamedLink/NamedLink';
import VerifiedBadge from '../VerifiedBadge/VerifiedBadge';

import css from './UroboroxListingCard.module.css';

// LISTING-CARD-FINAL — single source of truth for listing cards.
// Variants:
//   - 'default': whole card links to ListingPage
//   - 'owner':   no card-level link; renders an "Editar anuncio" CTA
// El bloque de 6 campos (sector, operativa, operación, facturación, empleados,
// fundada hace) vive en <ListingFieldsGrid>, compartido con la summary card de
// TransactionPage para que formato y copys sean idénticos.

const PinIcon = ({ className }) => (
  <svg
    className={className}
    width="14"
    height="14"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M12 2C7.589 2 4 5.589 4 9.995 4 15.672 12 22 12 22s8-6.328 8-12.005C20 5.589 16.411 2 12 2z"
      style={{ fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinejoin: 'round' }}
    />
    <circle
      cx="12"
      cy="10"
      r="2.5"
      style={{ fill: 'none', stroke: 'currentColor', strokeWidth: 1.5 }}
    />
  </svg>
);

const CardBody = ({ title, provinciaLabel, verificado, publicData, columns }) => (
  <div className={css.body}>
    <header className={css.header}>
      <h3 className={css.title}>{title}</h3>
      {provinciaLabel ? (
        <div className={css.location}>
          <PinIcon className={css.pinIcon} />
          <span className={css.locationText}>{provinciaLabel}</span>
        </div>
      ) : null}
      {verificado ? <VerifiedBadge size="md" iconOnlyMobile className={css.seal} /> : null}
    </header>

    <ListingFieldsGrid publicData={publicData} columns={columns} />
  </div>
);

const UroboroxListingCard = props => {
  const { listing, variant = 'default', columns = 3, className, rootClassName } = props;
  const id = listing?.id?.uuid;
  const attributes = listing?.attributes || {};
  const { title = '', publicData = {} } = attributes;
  const slug = createSlug(title);

  const provinciaLabel = PROVINCIA_LABELS[publicData.provincia] || publicData.provincia || '—';
  const verificado = !!publicData.verificado;

  const bodyProps = {
    title,
    provinciaLabel,
    verificado,
    publicData,
    columns,
  };

  const cardClasses = classNames(rootClassName || css.card, className);

  if (variant === 'owner') {
    const isDraft = attributes.state === LISTING_STATE_DRAFT;
    const editType = isDraft ? LISTING_PAGE_PARAM_TYPE_DRAFT : LISTING_PAGE_PARAM_TYPE_EDIT;
    return (
      <div className={cardClasses}>
        <CardBody {...bodyProps} />
        <NamedLink
          className={css.editLink}
          name="EditListingPage"
          params={{ id, slug, type: editType, tab: 'details' }}
        >
          Editar anuncio
        </NamedLink>
      </div>
    );
  }

  return (
    <NamedLink className={css.cardLink} name="ListingPage" params={{ id, slug }} ariaLabel={title}>
      <div className={cardClasses}>
        <CardBody {...bodyProps} />
      </div>
    </NamedLink>
  );
};

export default UroboroxListingCard;
